import _ from "lodash"
import { Range } from "rc-slider"
import "rc-slider/assets/index.css"
import React from "react"
import { attributeNameToFacetTitle, attributeValueToDate } from "../utils"
import styled from 'styled-components'
import { DateFacetHeuristicChart } from "./DateFacetHeuristicChart"
import { i18n } from "../constants"

const Wrapper = styled.div`
  .facet {
    .facet-date-container {
      padding: 0 0.5rem;

      /* Fix tooltip flicker */
      .google-visualization-tooltip {
          pointer-events: none;
      }

      /* Prevent tooltip text wrapping */
      .google-visualization-tooltip-item {
        white-space: nowrap;
      }
    }

    .facet-date-label-left {
      display: inline-block;
    }

    .facet-date-label-right {
      float: right;
    }

    .rc-slider {
      display: inline-block;
      width: 100%;

      &.with-number-input {
        width: calc(95% - 6rem);
        margin-right: 1.5rem;
      }

      .rc-slider-rail {
        position: absolute;
        height: 6px;
        border-radius: 5px;
        background-color: var(--grey);
        opacity: 0.7;
        transition: opacity 0.2s;
      }

      .rc-slider-track {
        position: absolute;
        height: 6px;
        border-radius: 5px;
        background-color: var(--blue);
      }

      .rc-slider-handle {
        width: 15px;
        height: 15px;
        border-radius: 50%;
        background: white;
        border: none;

        box-shadow: 0 0 2px 1px var(--green);

        &:focus {
          box-shadow: 0 0 2px 3px;
          var(--green);
        }
      }

      .rc-slider-dot {
        height: 6px;
        width: 6px;
        bottom: -2px;
        margin-left: -2px;
        box-shadow: 0 0 2px 1px var(--green);
        border: none;
      }

      .rc-slider-mark-text {
        color: var(--grey);

        font-size: 1.2rem;
        line-height: 1.2rem;

        transform: translateX(0.5rem) !important;
        white-space: nowrap;

        &.rc-slider-mark-text-active {
          color: var(--grey-3)
        }
      }
    }
  }
`

export class DateFacet extends React.Component {
  constructor(props) {
    super(props)

    this.state = this.getStateFromProps();
  }

  componentDidUpdate(prevProps) {
    const selectionsChanged = prevProps.selections !== this.props.selections
    const valuesChanged =
      prevProps.valueCountPairs !== this.props.valueCountPairs

    if (selectionsChanged || valuesChanged) {
      this.setState(this.getStateFromProps())
    }
  }

  getStateFromProps = () => {
    return {
      availableDateFacet: this.getAvailableDateFacetFromProps(),
      slider: {
        value: this.getSliderValueFromProps(),
      },
    }
  }

  getSliderValueFromProps = () => {
    const { selections } = this.props

    if (!selections) {
      return undefined
    }

    const sliderValue = [
      selections.min.get("year"),
      selections.max.get("year"),
    ]

    return sliderValue
  }

  getAvailableDateFacetFromProps = () => {
    const { valueCountPairs } = this.props

    if (_.isEmpty(valueCountPairs)) {
      return {
        maxYear: 0,
        minYear: 0,
        yearHeuristic: {},
      }
    }

    // Select min/max values and value heuristic map
    let minYear = Number.MAX_SAFE_INTEGER
    let maxYear = 0

    const yearHeuristic = {}

    for (const value of valueCountPairs) {
      const count = value?.Count || 0
      const dateValue = attributeValueToDate(value.DocumentAttributeValue)
      const yearValue = dateValue.get("year")

      if (yearValue < minYear) {
        minYear = yearValue
      }

      if (yearValue > maxYear) {
        maxYear = yearValue
      }

      yearHeuristic[yearValue] = _.get(yearHeuristic, yearValue, 0) + count
    }

    return {
      maxYear,
      minYear,
      yearHeuristic,
    }
  }

  handleChange = (sliderValue) => {
    this.setState({
      slider: {
        value: sliderValue,
      },
    })
  }

  applyChangedRangeFilter = () => {
    const { availableDateFacet } = this.state
    const { value } = this.state.slider

    if (!value) {
      return
    }

    const prevValue = this.getSliderValueFromProps()

    if (_.isUndefined(prevValue)) {
      // +1 because slider value min is exclusive, white available date facet is inclusive
      const isValueFullRange =
        value[0] + 1 <= availableDateFacet.minYear &&
        value[1] >= availableDateFacet.maxYear

      // There was previously no filter. Only set a filter if it
      // is not the full range
      if (!isValueFullRange) {
        this.props.onSelectionsChange(this.props.attributeName, value)
      }
    } else if (!_.isEqual(value, prevValue)) {
      this.props.onSelectionsChange(this.props.attributeName, value)
    }
  }

  clearFilter = () => {
    this.props.onSelectionsChange(this.props.attributeName, undefined)
  }

  // Get the full range to show on the slider
  //   Union of the available facet values and active
  //     selection
  //   Min is exclusive because slider needs one
  //     more tick than number of columns in chart
  //
  // @return [ exclusive min, inclusive max ]
  getFullSliderRange = () => {
    const { selections } = this.props
    const { availableDateFacet } = this.state

    if (selections) {
      return [
        Math.min(
          availableDateFacet.minYear - 1,
          selections.min.get("year")
        ),
        Math.max(
          availableDateFacet.maxYear,
          selections.max.get("year")
        )
      ];
    } else {
      return [
        availableDateFacet.minYear - 1,
        availableDateFacet.maxYear
      ];
    }
  };

  // Get the full range to show on the heuristic chart
  //   Union of the available facet values and active
  //     selection
  //
  // @return [ inclusive min, inclusive max ]
  getFullHeuristicChartRange = () => {
    const { selections } = this.props
    const { availableDateFacet } = this.state

    if (selections) {
      return [
        Math.min(
          availableDateFacet.minYear,
          selections.min.get("year") + 1
        ),
        Math.max(
          availableDateFacet.maxYear,
          selections.max.get("year")
        )
      ]
    } else {
      return [
        availableDateFacet.minYear,
        availableDateFacet.maxYear
      ]
    }
  }

  // Get the currently selected value range to display
  //
  // @return [ inclusive min, inclusive max ]
  getDisplaySelectionRange = () => {
    const { slider } = this.state

    if (slider.value) {
      // Slider min is exclusive, so +1 to make it inclusive
      return [
        slider.value[0] + 1,
        slider.value[1]
      ]
    } else {
      // Default to inclusive full range if no value is explicitly set
      return this.getFullHeuristicChartRange()
    }
  }

  render() {
    const {
      attributeName,
      disableAdd,
      valueCountPairs,
      selections,
    } = this.props
    const { availableDateFacet, slider } = this.state

    if (_.isEmpty(valueCountPairs)) {
      // Cannot generate a valid render without any values
      return null
    }

    const active = !_.isUndefined(selections)

    const fullSliderRange = this.getFullSliderRange()
    const fullHeuristicChartRange = this.getFullHeuristicChartRange()
    const displaySelectionRange = this.getDisplaySelectionRange()

    return (
      <Wrapper className="facet">
        <div className="facet-title">
          {attributeNameToFacetTitle(attributeName)}&nbsp;
          {active && (
            <span onClick={this.clearFilter} className="action-link">
              {i18n.CLEAR}
            </span>
          )}
        </div>
        <div className="facet-date-container">
          <div className="facet-heuristic-chart">
            <DateFacetHeuristicChart
              availableDateFacet={availableDateFacet}
              fullRange={fullHeuristicChartRange}
              displaySelectionRange={displaySelectionRange}
            />
          </div>
          <Range
            allowCross={false}
            pushable={1}
            min={fullSliderRange[0]}
            max={fullSliderRange[1]}
            value={slider.value || fullSliderRange}
            onChange={this.handleChange}
            onAfterChange={this.applyChangedRangeFilter}
            disabled={disableAdd && !active}
          />
          <div>
            <div className="facet-date-label-left">{displaySelectionRange[0]}</div>
            <div className="facet-date-label-right">{displaySelectionRange[1]}</div>
          </div>
        </div>
      </Wrapper>
    )
  }
}



// WEBPACK FOOTER //
// ./src/containers/Kendra/facets/components/DateFacet.js